import React, { useState } from 'react';

const Instructions = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'units', label: 'Units' },
    { id: 'combat', label: 'Combat' },
    { id: 'tips', label: 'Tips' }
  ];

  return (
    <div className="text-white">
      <div className="flex border-b border-gray-600">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`px-4 py-2 ${
              activeTab === tab.id 
                ? 'border-b-2 border-green-500 text-green-400 font-bold' 
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      <div className="mt-4">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'units' && <UnitsTab />}
        {activeTab === 'combat' && <CombatTab />}
        {activeTab === 'tips' && <TipsTab />}
      </div>
    </div>
  );
};

const OverviewTab = () => (
  <div>
    <h3 className="text-lg font-bold mb-2">Objective</h3>
    <p className="mb-4">
      Push through enemy defenses by strategically deploying units. 
      Destroy all enemy units or advance the battle line to the enemy base to win.
    </p>
    
    <h3 className="text-lg font-bold mb-2">Gameplay Cycle</h3>
    <ul className="space-y-2 pl-5 list-disc">
      <li>Deploy units in the <span className="text-green-400 font-semibold">deployment zone</span> (green area)</li>
      <li>Press "Start Battle" when ready</li>
      <li>Units automatically move and attack</li>
      <li>Win by pushing the battle line to 95% or defeating all enemies</li>
      <li>Surviving units carry over to the next stage</li>
      <li>If you lose, it's game over - this is a hardcore strategy game!</li>
    </ul>
    
    <h3 className="text-lg font-bold mt-4 mb-2">Battle Line</h3>
    <p>
      The <span className="text-yellow-400 font-semibold">battle line</span> shows your progress. 
      Push it to the enemy base (95%) to win. If enemies push it back to your base (5%), you lose.
    </p>
  </div>
);

const UnitsTab = () => (
  <div>
    <h3 className="text-lg font-bold mb-2">Player Units</h3>
    
    <div className="space-y-4">
      <div className="border border-gray-600 rounded p-3">
        <h4 className="font-bold flex items-center">
          <span className="mr-2">♟️</span> Pawn
          <span className="ml-2 text-xs bg-green-800 px-2 py-0.5 rounded">50 currency</span>
        </h4>
        <p className="text-sm mb-2">Fast melee unit with low damage but decent health.</p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>HP: 300</div>
          <div>Attack: 15</div>
          <div>Defense: 1</div>
          <div>Range: 20</div>
          <div>Speed: 2.8</div>
          <div>Type: Melee</div>
        </div>
      </div>
      
      <div className="border border-gray-600 rounded p-3">
        <h4 className="font-bold flex items-center">
          <span className="mr-2">🛡️</span> Footman
          <span className="ml-2 text-xs bg-green-800 px-2 py-0.5 rounded">75 currency</span>
        </h4>
        <p className="text-sm mb-2">Balanced infantry unit with good health and damage.</p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>HP: 450</div>
          <div>Attack: 22</div>
          <div>Defense: 4</div>
          <div>Range: 25</div>
          <div>Speed: 1.8</div>
          <div>Type: Melee</div>
        </div>
      </div>
      
      <div className="border border-gray-600 rounded p-3">
        <h4 className="font-bold flex items-center">
          <span className="mr-2">🏹</span> Archer
          <span className="ml-2 text-xs bg-green-800 px-2 py-0.5 rounded">150 currency</span>
        </h4>
        <p className="text-sm mb-2">Ranged attacker with high damage but low health.</p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>HP: 320</div>
          <div>Attack: 35</div>
          <div>Defense: 1</div>
          <div>Range: 200</div>
          <div>Speed: 1.5</div>
          <div>Type: Ranged</div>
        </div>
      </div>
    </div>
    
    <h3 className="text-lg font-bold mt-4 mb-2">Enemy Units</h3>
    <p>You'll face various enemy units including:</p>
    <ul className="pl-5 list-disc space-y-1">
      <li><span className="text-yellow-400">🔥 Torch Swingers</span> - Fast melee units</li>
      <li><span className="text-yellow-400">💣 Bombard Crews</span> - Ranged siege units</li>
      <li><span className="text-yellow-400">🛢️ Explosive Barrels</span> - Kamikaze units</li>
      <li><span className="text-yellow-400">🗼 Wood Towers</span> - Defensive structures</li>
    </ul>
  </div>
);

const CombatTab = () => (
  <div>
    <h3 className="text-lg font-bold mb-2">Battle Mechanics</h3>
    
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold mb-1">Unit Behavior</h4>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>Units automatically move and attack after deployment</li>
          <li>Units target the nearest enemy first</li>
          <li>Attack damage is reduced by the target's defense value</li>
          <li>Units have different ranges, speeds, and attack cooldowns</li>
          <li>Structures like towers cannot move but have high health and range</li>
        </ul>
      </div>
      
      <div>
        <h4 className="font-semibold mb-1">Battle Line</h4>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>The yellow dashed line shows the current battle position</li>
          <li>Initially positioned at the battlefield center (50%)</li>
          <li>Your units advancing push it forward</li>
          <li>Enemy units advancing push it backward</li>
          <li>Victory: reach 95% (enemy base)</li>
          <li>Defeat: pushed back to 5% (your base)</li>
        </ul>
      </div>
      
      <div>
        <h4 className="font-semibold mb-1">Unit Types</h4>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li><span className="text-yellow-400">Melee:</span> Short range, good for close combat</li>
          <li><span className="text-yellow-400">Ranged:</span> Long range, good damage but less health</li>
          <li><span className="text-yellow-400">Siege:</span> Specialized against structures</li>
          <li><span className="text-yellow-400">Structure:</span> Immobile but high health and range</li>
        </ul>
      </div>
    </div>
  </div>
);

const TipsTab = () => (
  <div>
    <h3 className="text-lg font-bold mb-2">Tips & Tricks</h3>
    
    <ul className="space-y-2 pl-5 list-disc">
      <li>Balance your army composition - don't rely on a single unit type</li>
      <li>Place <span className="text-yellow-400 font-semibold">Archer</span> units behind your melee units</li>
      <li>Use <span className="text-yellow-400 font-semibold">Footmen</span> to form a defensive line and protect your ranged units</li>
      <li>Deploy <span className="text-yellow-400 font-semibold">Pawn</span> units to quickly advance and overwhelm enemy positions</li>
      <li>Prioritize destroying enemy towers to make advancement easier</li>
      <li>Save some currency between stages for later levels</li>
      <li>Hover over units to inspect their stats</li>
      <li>Press <kbd className="bg-gray-700 px-1 rounded border border-gray-500">X</kbd> to delete a unit during placement</li>
    </ul>
    
    <h3 className="text-lg font-bold mt-4 mb-2">Rewards System</h3>
    <ul className="space-y-1 pl-5 list-disc">
      <li>Base reward after victory: 200 currency</li>
      <li>Performance bonus: Based on battle line position</li>
      <li>Stage bonus: Based on level and stage number</li>
      <li>Surviving units carry over to the next stage</li>
    </ul>
  </div>
);

export default Instructions;